import { ICompassusNavigatorRepository } from "../../adapters/driven/repositories/compassusNavigatorRepository";
import { stripCompassusVirtualTodoTags } from "../../../util/compassusTodoTags";

function hospitalizationError(error: unknown): ICompassusHospitalizationError {
  const candidate = error as {
    message?: string;
    status?: number;
    response?: {
      status?: number;
      data?: {
        detail?: string | { message?: string };
        message?: string;
      };
    };
  };
  const detail = candidate.response?.data?.detail;
  const detailMessage = typeof detail === "string" ? detail : detail?.message;
  return {
    message:
      detailMessage ??
      candidate.response?.data?.message ??
      candidate.message ??
      "Hospitalization request failed",
    status: candidate.response?.status ?? candidate.status,
  };
}

/**
 * The shared API provider resolves HTTP failures with the error response body
 * instead of throwing, so a FastAPI `{ detail: ... }` payload reaches the
 * service layer looking like data. Extracts the error message from such a
 * body, or returns null when the value doesn't look like an error body.
 */
function extractHospitalizationErrorMessage(response: unknown): string | null {
  if (!response || typeof response !== "object") return null;
  const { detail, message } = response as {
    detail?: unknown;
    message?: unknown;
  };
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const nested = (detail as { message?: unknown }).message;
    return typeof nested === "string"
      ? nested
      : "Hospitalization request failed";
  }
  if (typeof message === "string") return message;
  return null;
}

function assertHospitalizationRecordResponse(
  response: unknown
): ICompassusHospitalizationRecord {
  const record = response as ICompassusHospitalizationRecord | undefined;
  if (typeof record?.id !== "number") {
    throw new Error(
      extractHospitalizationErrorMessage(response) ??
        "Hospitalization request failed"
    );
  }
  return record;
}

function isGenerationStatusRunning(
  status: ICompassusGenerationStatusResponse | undefined | null
): boolean {
  const rawStatus = status?.generation_status ?? status?.status ?? null;
  if (typeof rawStatus === "string") return rawStatus === "running";
  if (!rawStatus || typeof rawStatus !== "object") return false;
  return (
    rawStatus.status === "running" || rawStatus.generation_status === "running"
  );
}

function buildRunningHospiceLlmRefreshResponse(
  status: ICompassusGenerationStatusResponse,
  patientId: number
): ICompassusPatientHospiceLlmRiskRefreshResponse {
  return {
    patient_id: status.patient_id ?? patientId,
    clinic_id: status.clinic_id ?? null,
    status: "running",
    hospice_rule_total_score: null,
    llm_risk_score: null,
    llm_evidence_json: null,
    llm_generated_at: null,
    generation_status: status.generation_status ?? status.status ?? null,
  };
}

function assertHhCadenceResponse(
  response: unknown,
  allowNull: boolean
): ICompassusHhCadence | null {
  if (response == null && allowNull) return null;
  const candidate = response as
    | (Partial<ICompassusHhCadence> & {
        detail?: string | { message?: string };
        message?: string;
      })
    | null;
  if (
    candidate &&
    Number.isInteger(candidate.patient_id) &&
    Number(candidate.patient_id) > 0 &&
    (candidate.cadence_days === 7 || candidate.cadence_days === 14) &&
    typeof candidate.start_date === "string"
  ) {
    return candidate as ICompassusHhCadence;
  }
  const detail =
    typeof candidate?.detail === "string"
      ? candidate.detail
      : candidate?.detail?.message;
  throw new Error(
    detail ?? candidate?.message ?? "Invalid HH cadence response"
  );
}

function extractApiErrorMessage(response: unknown): string | null {
  if (!response || typeof response !== "object") return null;
  const readMessage = (value: unknown): string | null => {
    if (typeof value === "string" && value.trim()) return value;
    if (!value || typeof value !== "object") return null;
    const nested = value as { message?: unknown; detail?: unknown };
    return readMessage(nested.message) ?? readMessage(nested.detail);
  };
  const candidate = response as {
    detail?: unknown;
    error?: unknown;
    message?: unknown;
  };
  for (const value of [candidate.detail, candidate.error, candidate.message]) {
    const message = readMessage(value);
    if (message) return message;
  }
  return null;
}

function assertHhVisitScheduleResponse<
  T extends
    | ICompassusHhVisitScheduleResponse
    | ICompassusHhVisitCancelResponse
    | ICompassusHhFollowUpResponse
>(
  response: unknown,
  options?: { cadenceRequired?: boolean; changedRequired?: boolean }
): T {
  const errorMessage = extractApiErrorMessage(response);
  if (errorMessage) throw new Error(errorMessage);
  const candidate = response as Partial<T> | null;
  const assignment = candidate?.assignment;
  if (
    !assignment ||
    !Number.isInteger(assignment.id) ||
    assignment.id <= 0 ||
    (assignment.visit_phase !== "initial" &&
      assignment.visit_phase !== "follow_up") ||
    !Array.isArray(candidate.notification_warnings)
  ) {
    throw new Error("Invalid HH visit scheduling response");
  }
  const changed = (candidate as Partial<ICompassusHhVisitScheduleResponse>)
    .changed;
  if (
    (options?.changedRequired || "changed" in (candidate as object)) &&
    typeof changed !== "boolean"
  ) {
    throw new Error("Invalid HH visit scheduling response");
  }
  const cadence = (candidate as Partial<ICompassusHhFollowUpResponse>).cadence;
  if (options?.cadenceRequired) {
    assertHhCadenceResponse(cadence, false);
  } else if (cadence != null) {
    assertHhCadenceResponse(cadence, false);
  }
  return candidate as T;
}

export const CompassusNavigatorServiceImplFactory = ({
  compassusNavigatorRepository,
}: {
  compassusNavigatorRepository: ICompassusNavigatorRepository;
}) => {
  const getPatients: IGetCompassusPatients = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getPatientsAPI({
        clinic_id: clinicId,
        params: queryParams,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientIds: IGetCompassusPatientIds = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getPatientIdsAPI({
        clinic_id: clinicId,
        params: queryParams,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCompletedVisits: IGetCompassusCompletedVisits = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getCompletedVisitsAPI(
        {
          clinic_id: clinicId,
          params: queryParams,
        }
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const exportNavigator: IExportCompassusNavigator = async ({
    clinicId,
    body,
  }) => {
    try {
      const response = await compassusNavigatorRepository.exportNavigatorAPI({
        clinic_id: clinicId,
        body,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCounts: IGetCompassusCounts = async ({ clinicId, queryParams }) => {
    try {
      const response = await compassusNavigatorRepository.getCountsAPI({
        clinic_id: clinicId,
        params: queryParams,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPayerFilters: IGetCompassusPayerFilters = async ({ clinicId }) => {
    try {
      const response = await compassusNavigatorRepository.getPayerFiltersAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getMedicationFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getMedicationFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getAllergyFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getAllergyFiltersAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getConditionFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getConditionFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getLastVisitProviderFilters: IGetCompassusProviderFilters = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getLastVisitProviderFiltersAPI({
          clinic_id: clinicId,
          params: queryParams,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getNextVisitProviderFilters: IGetCompassusProviderFilters = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getNextVisitProviderFiltersAPI({
          clinic_id: clinicId,
          params: queryParams,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getTodoTagFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getTodoTagFiltersAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getCustomTagFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getCustomTagFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getClinicalRiskScoreFilters: IGetCompassusClinicalRiskScoreFilters =
    async ({ clinicId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getClinicalRiskScoreFiltersAPI({
            clinic_id: clinicId,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const patchPatientTodo: IPatchCompassusPatientTodo = async ({
    clinicId,
    patientId,
    todoTags,
  }) => {
    try {
      const response = await compassusNavigatorRepository.patchPatientTodoAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        body: { tags: stripCompassusVirtualTodoTags(todoTags) },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const patchPatientHhJourneyStatus: IPatchCompassusPatientHhJourneyStatus =
    async ({ clinicId, patientId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.patchPatientHhJourneyStatusAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            body,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientHhJourneyStatus: IGetCompassusPatientHhJourneyStatus =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientHhJourneyStatusAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getHhCadence: IGetCompassusHhCadence = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const data = await compassusNavigatorRepository.getPatientHhCadenceAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return { success: true, data: assertHhCadenceResponse(data, true) };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const patchHhCadence: IPatchCompassusHhCadence = async ({
    clinicId,
    patientId,
    body,
  }) => {
    try {
      const data = await compassusNavigatorRepository.patchPatientHhCadenceAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        body,
      });
      return {
        success: true,
        data: assertHhCadenceResponse(data, false) as ICompassusHhCadence,
      };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientDataReview: IGetCompassusPatientDataReview = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientDataReviewAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientInsurance: IGetCompassusPatientInsurance = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientInsuranceAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      if (!response || typeof response.patient_id !== "number") {
        throw new Error("Unable to load patient insurance information");
      }
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientInsuranceRawResponse: IGetCompassusPatientInsuranceRawResponse =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientInsuranceRawResponseAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        if (
          !response ||
          typeof response.eligibility_record_id !== "number" ||
          !Object.prototype.hasOwnProperty.call(response, "response")
        ) {
          throw new Error("Unable to load the stored eligibility response");
        }
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getHhEligibilityWorklist: IGetCompassusHhEligibilityWorklist = async ({
    clinicId,
    params,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getHhEligibilityWorklistAPI({
          clinic_id: clinicId,
          params,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getHhNpWorklist: IGetCompassusHhNpWorklist = async ({
    clinicId,
    params,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getHhNpWorklistAPI({
        clinic_id: clinicId,
        params,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getHhTechnicianWorklist: IGetCompassusHhTechnicianWorklist = async ({
    clinicId,
    params,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getHhTechnicianWorklistAPI({
          clinic_id: clinicId,
          params,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getHhGlobalAdminWorklist: IGetCompassusHhGlobalAdminWorklist = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getHhGlobalAdminWorklistAPI({
          clinic_id: clinicId,
          params: queryParams,
        });
      if (!response || !Array.isArray(response.items)) {
        throw new Error("Invalid Global Admin worklist response");
      }
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getHhGlobalAdminWorklistCounts: IGetCompassusHhGlobalAdminWorklistCounts =
    async ({ clinicId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.postHhGlobalAdminWorklistCountsAPI(
            {
              clinic_id: clinicId,
              body,
            }
          );
        if (!response || !Array.isArray(response.counts)) {
          throw new Error("Invalid Global Admin count response");
        }
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const patchHhNpUnlinkDraft: IPatchCompassusHhNpUnlinkDraft = async ({
    clinicId,
    assignmentId,
    ipvNoteId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.patchHhNpUnlinkDraftAPI({
          clinic_id: clinicId,
          assignment_id: assignmentId,
          body: { ipv_note_id: ipvNoteId },
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const rescheduleHhVisitAssignment: IRescheduleCompassusHhVisitAssignment =
    async ({ clinicId, patientId, assignmentId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.patchHhVisitScheduleAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            assignment_id: assignmentId,
            body,
          });
        return {
          success: true,
          data: assertHhVisitScheduleResponse<ICompassusHhVisitScheduleResponse>(
            response,
            { changedRequired: true }
          ),
        };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const cancelHhVisitAssignment: ICancelCompassusHhVisitAssignment = async ({
    clinicId,
    patientId,
    assignmentId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.deleteHhVisitAssignmentAPI({
          clinic_id: clinicId,
          patient_id: patientId,
          assignment_id: assignmentId,
        });
      return {
        success: true,
        data: assertHhVisitScheduleResponse<ICompassusHhVisitCancelResponse>(
          response
        ),
      };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const createHhFollowUp: ICreateCompassusHhFollowUp = async ({
    clinicId,
    patientId,
    body,
  }) => {
    try {
      const response = await compassusNavigatorRepository.postHhFollowUpAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        body,
      });
      return {
        success: true,
        data: assertHhVisitScheduleResponse<ICompassusHhFollowUpResponse>(
          response,
          { cadenceRequired: body.cadence_days != null }
        ),
      };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const createHhAtRiskFollowUp: ICreateCompassusHhAtRiskFollowUp = async ({
    clinicId,
    patientId,
    body,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.postHhAtRiskFollowUpAPI({
          clinic_id: clinicId,
          patient_id: patientId,
          body,
        });
      return {
        success: true,
        data: assertHhVisitScheduleResponse<ICompassusHhAtRiskFollowUpResponse>(
          response,
          { cadenceRequired: body.cadence_days != null }
        ),
      };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const patchPatientCustomTags: IPatchCompassusPatientCustomTags = async ({
    clinicId,
    patientId,
    tags,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.postPatientCustomTagsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
          body: { tags },
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientCustomTags: IGetCompassusPatientCustomTags = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientCustomTagsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getHospitalizationRecords: IGetCompassusHospitalizationRecords =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getHospitalizationRecordsAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        if (!Array.isArray(response?.data)) {
          throw new Error(
            extractHospitalizationErrorMessage(response) ??
              "Hospitalization request failed"
          );
        }
        return { success: true, data: response.data };
      } catch (error) {
        return { success: false, error: hospitalizationError(error) };
      }
    };

  const createHospitalizationRecord: ICreateCompassusHospitalizationRecord =
    async ({ clinicId, patientId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.createHospitalizationRecordAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            body,
          });
        return {
          success: true,
          data: assertHospitalizationRecordResponse(response),
        };
      } catch (error) {
        return { success: false, error: hospitalizationError(error) };
      }
    };

  const updateHospitalizationRecord: IUpdateCompassusHospitalizationRecord =
    async ({ clinicId, patientId, recordId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.updateHospitalizationRecordAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            record_id: recordId,
            body,
          });
        return {
          success: true,
          data: assertHospitalizationRecordResponse(response),
        };
      } catch (error) {
        return { success: false, error: hospitalizationError(error) };
      }
    };

  const deleteHospitalizationRecord: IDeleteCompassusHospitalizationRecord =
    async ({ clinicId, patientId, recordId }) => {
      try {
        const response =
          await compassusNavigatorRepository.deleteHospitalizationRecordAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            record_id: recordId,
          });
        // A successful delete is an empty 204; anything with an error message
        // is a swallowed HTTP failure.
        const errorMessage = extractHospitalizationErrorMessage(response);
        if (errorMessage !== null) {
          throw new Error(errorMessage);
        }
        return { success: true };
      } catch (error) {
        return { success: false, error: hospitalizationError(error) };
      }
    };

  const getPatientAssociations: IGetCompassusPatientAssociations = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientAssociationsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  /**
   * Bulk to-do mutation — one HTTP request for any number of patients. See
   * `IBulkPatchCompassusPatientTodo` in the driven port for mode semantics.
   */
  const bulkPatchPatientTodo: IBulkPatchCompassusPatientTodo = async ({
    clinicId,
    patientIds,
    mode,
    todoTags,
  }) => {
    try {
      const body: ICompassusBulkPatchTodoRequest = {
        patient_ids: patientIds,
        mode,
      };
      // Backend ignores todo_tags when mode="clear", but omitting avoids
      // sending a superfluous field and matches the spec's "can be omitted".
      if (mode !== "clear") {
        body.todo_tags = stripCompassusVirtualTodoTags(todoTags ?? []);
      }
      const response =
        await compassusNavigatorRepository.bulkPatchPatientTodoAPI({
          clinic_id: clinicId,
          body,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const putWatchlist: IPutCompassusWatchlist = async ({ clinicId, items }) => {
    try {
      const response = await compassusNavigatorRepository.putWatchlistAPI({
        clinic_id: clinicId,
        body: { items },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const patchWatchlist: IPatchCompassusWatchlist = async ({
    clinicId,
    add,
    remove,
  }) => {
    try {
      const response = await compassusNavigatorRepository.patchWatchlistAPI({
        clinic_id: clinicId,
        body: { add, remove },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getAcuityLevelFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getAcuityLevelFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientAcuityLevelHistory: IGetCompassusPatientAcuityLevelHistory =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientAcuityLevelHistoryAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientPrognosisHistory: IGetCompassusPatientPrognosisHistory =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientPrognosisHistoryAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientFormHistory: IGetCompassusPatientFormHistory = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientFormHistoryAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientHospiceEligibilityScore: IGetCompassusPatientHospiceEligibilityScore =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientHospiceEligibilityScoreAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientHospiceEligibilityScoreHistory: IGetCompassusPatientHospiceEligibilityScoreHistory =
    async ({ patientId, includeRubric, limit }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientHospiceEligibilityScoreHistoryAPI(
            {
              patient_id: patientId,
              include_rubric: includeRubric,
              limit,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientHospiceEligibilityScoreLlmRefreshStatus: IGetCompassusPatientHospiceEligibilityScoreLlmRefreshStatus =
    async ({ patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientHospiceEligibilityScoreLlmRefreshStatusAPI(
            {
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const refreshPatientHospiceEligibilityScoreLlm: IRefreshCompassusPatientHospiceEligibilityScoreLlm =
    async ({ patientId }) => {
      try {
        try {
          const status =
            await compassusNavigatorRepository.getPatientHospiceEligibilityScoreLlmRefreshStatusAPI(
              {
                patient_id: patientId,
              }
            );
          if (isGenerationStatusRunning(status)) {
            return {
              success: true,
              data: buildRunningHospiceLlmRefreshResponse(status, patientId),
            };
          }
        } catch (statusError) {
          console.warn(
            "[CompassusNavigatorService] Unable to check hospice LLM refresh status before POST:",
            statusError
          );
        }

        const response =
          await compassusNavigatorRepository.postPatientHospiceEligibilityScoreLlmRefreshAPI(
            {
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getFormsSchema: IGetCompassusFormsSchema = async ({ clinicId }) => {
    try {
      const response = await compassusNavigatorRepository.getFormsSchemaAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientCommunicationNumbers: IGetCompassusPatientCommunicationNumbers =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientCommunicationNumbersAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const linkPatientCommunicationNumber: ILinkCompassusPatientCommunicationNumber =
    async ({ clinicId, patientId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.postPatientCommunicationNumberAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            body,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const setPrimaryPatientCommunicationNumber: ISetPrimaryCompassusPatientCommunicationNumber =
    async ({ clinicId, patientId, phoneNumber }) => {
      try {
        const response =
          await compassusNavigatorRepository.putPatientCommunicationPrimaryNumberAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
              body: { phone_number: phoneNumber },
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const updatePatientCommunicationNumberLabel: IUpdateCompassusPatientCommunicationNumberLabel =
    async ({ clinicId, patientId, linkId, label }) => {
      try {
        const response =
          await compassusNavigatorRepository.patchPatientCommunicationNumberAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
              link_id: linkId,
              body: { label },
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const removePatientCommunicationNumber: IRemoveCompassusPatientCommunicationNumber =
    async ({ clinicId, patientId, linkId }) => {
      try {
        const response =
          await compassusNavigatorRepository.deletePatientCommunicationNumberAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
              link_id: linkId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getPatientCommunicationSms: IGetCompassusPatientCommunicationSms =
    async ({ clinicId, patientId, phoneNumber, limit }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientCommunicationSmsAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            phone_number: phoneNumber,
            limit,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getPatientCommunicationSmsThreads: IGetCompassusPatientCommunicationSmsThreads =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientCommunicationSmsThreadsAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const sendPatientCommunicationSms: ISendCompassusPatientCommunicationSms =
    async ({ clinicId, patientId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.postPatientCommunicationSmsAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            body,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const sendNpAppointmentSms: ISendCompassusNpAppointmentSms = async ({
    clinicId,
    patientId,
    body,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.postPatientNpAppointmentSmsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
          body,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientCommunicationCalls: IGetCompassusPatientCommunicationCalls =
    async ({ clinicId, patientId, limit }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientCommunicationCallsAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            limit,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getPatientCommunicationCallToken: IGetCompassusPatientCommunicationCallToken =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.postPatientCommunicationCallTokenAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const startPatientCommunicationCall: IStartCompassusPatientCommunicationCall =
    async ({ clinicId, patientId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.postPatientCommunicationCallAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            body,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getHhRpmCommunicationNotificationCount: IGetHhRpmCommunicationNotificationCount =
    async ({ clinicId }) => {
      try {
        const data =
          await compassusNavigatorRepository.getHhRpmCommunicationNotificationCountAPI(
            { clinic_id: clinicId }
          );
        return { success: true, data };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getHhRpmCommunicationThreads: IGetHhRpmCommunicationThreads = async ({
    clinicId,
    filter,
    limit,
    offset,
  }) => {
    try {
      const data =
        await compassusNavigatorRepository.getHhRpmCommunicationThreadsAPI({
          clinic_id: clinicId,
          filter,
          limit,
          offset,
        });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getHhRpmNotificationEpisodes: IGetHhRpmNotificationEpisodes = async ({
    clinicId,
    status,
    notificationType,
    limit,
    offset,
  }) => {
    try {
      const data =
        await compassusNavigatorRepository.getHhRpmNotificationEpisodesAPI({
          clinic_id: clinicId,
          status,
          notification_type: notificationType,
          limit,
          offset,
        });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const reviewHhRpmCommunicationNotification: IReviewHhRpmCommunicationNotification =
    async ({ clinicId, episodeId, throughEventId }) => {
      try {
        const data =
          await compassusNavigatorRepository.postHhRpmCommunicationReviewAPI({
            clinic_id: clinicId,
            episode_id: episodeId,
            through_event_id: throughEventId,
          });
        return { success: true, data };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getHhRpmSchedulerAlertRecipients: IGetHhRpmSchedulerAlertRecipients =
    async ({ clinicId }) => {
      try {
        const data =
          await compassusNavigatorRepository.getHhRpmSchedulerAlertRecipientsAPI(
            { clinic_id: clinicId }
          );
        return { success: true, data };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const createHhRpmSchedulerAlertRecipient: ICreateHhRpmSchedulerAlertRecipient =
    async ({ clinicId, body }) => {
      try {
        const data =
          await compassusNavigatorRepository.postHhRpmSchedulerAlertRecipientAPI(
            { clinic_id: clinicId, body }
          );
        return { success: true, data };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const removeHhRpmSchedulerAlertRecipient: IRemoveHhRpmSchedulerAlertRecipient =
    async ({ clinicId, recipientId }) => {
      try {
        const data =
          await compassusNavigatorRepository.deleteHhRpmSchedulerAlertRecipientAPI(
            { clinic_id: clinicId, recipient_id: recipientId }
          );
        return { success: true, data };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  return {
    getPatients,
    getPatientIds,
    getCompletedVisits,
    exportNavigator,
    getCounts,
    getPayerFilters,
    getMedicationFilters,
    getAllergyFilters,
    getConditionFilters,
    getLastVisitProviderFilters,
    getNextVisitProviderFilters,
    getTodoTagFilters,
    getCustomTagFilters,
    getClinicalRiskScoreFilters,
    patchPatientTodo,
    getPatientHhJourneyStatus,
    patchPatientHhJourneyStatus,
    getHhCadence,
    patchHhCadence,
    getPatientDataReview,
    getPatientInsurance,
    getPatientInsuranceRawResponse,
    getHhEligibilityWorklist,
    getHhNpWorklist,
    getHhTechnicianWorklist,
    getHhGlobalAdminWorklist,
    getHhGlobalAdminWorklistCounts,
    patchHhNpUnlinkDraft,
    rescheduleHhVisitAssignment,
    cancelHhVisitAssignment,
    createHhFollowUp,
    createHhAtRiskFollowUp,
    patchPatientCustomTags,
    getPatientCustomTags,
    getHospitalizationRecords,
    createHospitalizationRecord,
    updateHospitalizationRecord,
    deleteHospitalizationRecord,
    getPatientAssociations,
    bulkPatchPatientTodo,
    putWatchlist,
    patchWatchlist,
    getAcuityLevelFilters,
    getPatientAcuityLevelHistory,
    getPatientPrognosisHistory,
    getPatientFormHistory,
    getPatientHospiceEligibilityScore,
    getPatientHospiceEligibilityScoreHistory,
    getPatientHospiceEligibilityScoreLlmRefreshStatus,
    refreshPatientHospiceEligibilityScoreLlm,
    getFormsSchema,
    getPatientCommunicationNumbers,
    linkPatientCommunicationNumber,
    setPrimaryPatientCommunicationNumber,
    updatePatientCommunicationNumberLabel,
    removePatientCommunicationNumber,
    getPatientCommunicationSms,
    getPatientCommunicationSmsThreads,
    sendPatientCommunicationSms,
    sendNpAppointmentSms,
    getPatientCommunicationCalls,
    getPatientCommunicationCallToken,
    startPatientCommunicationCall,
    getHhRpmCommunicationNotificationCount,
    getHhRpmCommunicationThreads,
    getHhRpmNotificationEpisodes,
    reviewHhRpmCommunicationNotification,
    getHhRpmSchedulerAlertRecipients,
    createHhRpmSchedulerAlertRecipient,
    removeHhRpmSchedulerAlertRecipient,
  };
};
